import { filesService } from '../../services/FilesService';
import { tenantUploadService } from '../../services/TenantUploadService';
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  Stack,
  Alert,
} from '@mui/material';
import { AppIconButton } from '../design-system';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import CompactUploadButton, { COMPACT_UPLOAD_LABELS } from './CompactUploadButton';
import UploadSourceButton from './UploadSourceButton';
import TempFilePickerDialog from './TempFilePickerDialog';
import type { PendingTempFileAssociation } from './file-upload.types';
import { isStagedTempFileKey } from '../../utils/tempFileKeys';
import type { TempFileMetadata } from '@cnapse/shared/temp-file-schemas.js';
import { tempFileMatchesMediaKind, type TempFileMediaKind } from './tempFileBrowserUtils';

import { type FileMetadata } from '../../../../shared/src/schemas';
import * as dt from '@cnapse/shared/data.types.js';
import {
  isTrackedUploadAllowedImageFile,
  TRACKED_UPLOAD_IMAGE_ACCEPT,
} from '@cnapse/shared/file-upload.constants.js';
import { useBootstrap } from '../../contexts/BootstrapContext';

export interface UploadedFileMetadata extends FileMetadata {
  fileId?: number;
  fromTempFile?: boolean;
}

export interface UploadedFile {
  url: string;
  metadata: UploadedFileMetadata;
}

interface FileUploadProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  accept?: string;
  maxFiles?: number;
  multiple?: boolean;
  label?: string;
  disabled?: boolean;
  showPreview?: boolean;
  /** Compact button-style uploader instead of large drop zone. */
  compact?: boolean;
  /** When false, parent renders file list / thumbnails (common with compact). */
  showFileList?: boolean;
  /** Hide max-files / accept hint beside compact button (e.g. when placed in a section header). */
  compactHideHint?: boolean;
  /** On mobile, open device camera instead of file browser for images. */
  captureEnvironment?: boolean;
  type?: 'images' | 'files' | 'both';
  entityType?: string;
  entityId?: number;
  fileCategory?: 'image' | 'document' | 'fingerprint_image';
  allowTempFilePick?: boolean;
  pendingTempFileAssociations?: PendingTempFileAssociation[];
  onPendingTempFileAssociationsChange?: (pending: PendingTempFileAssociation[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
  onUploadErrorChange?: (error: string | null) => void;
}

function resolveTempFileTargetCategory(
  fileCategory: FileUploadProps['fileCategory'],
  mediaKind: TempFileMediaKind,
): dt.FILE_CATEGORY {
  if (fileCategory === 'fingerprint_image') return dt.FILE_CATEGORY.FingerprintImage;
  if (fileCategory === 'document' || mediaKind === 'document') return dt.FILE_CATEGORY.Document;
  return dt.FILE_CATEGORY.Image;
}

const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  accept,
  maxFiles = 10,
  multiple = true,
  label,
  disabled = false,
  showPreview = true,
  compact = false,
  showFileList,
  compactHideHint = false,
  captureEnvironment,
  type = 'both',
  entityType,
  entityId,
  fileCategory,
  allowTempFilePick = false,
  pendingTempFileAssociations = [],
  onPendingTempFileAssociationsChange,
  onUploadingChange,
  onUploadErrorChange,
}) => {
  const shouldShowFileList = showFileList ?? !compact;
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  useEffect(() => {
    onUploadErrorChange?.(error);
  }, [error, onUploadErrorChange]);
  const [allowedTypes, setAllowedTypes] = useState<string>('');
  const [tempFilePickerOpen, setTempFilePickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tempFileMediaKind =
    type === 'images'
      ? 'image'
      : type === 'files'
        ? 'document'
        : fileCategory === 'image' || fileCategory === 'fingerprint_image'
          ? 'image'
          : fileCategory === 'document'
            ? 'document'
            : 'any';
  const tempFilePickerTitle =
    tempFileMediaKind === 'image'
      ? 'Browse temp images'
      : tempFileMediaKind === 'document'
        ? 'Browse temp documents'
        : 'Browse temp files';

  const { config: bootstrapConfig } = useBootstrap();

  useEffect(() => {
    const types = bootstrapConfig.fileUpload.allowedTypes;
    if (types) {
      setAllowedTypes(types);
    }
  }, [bootstrapConfig.fileUpload.allowedTypes]);

  const formatUploadFailureLine = (
    failure: { displayName?: string; originalName: string; error: string },
    localName?: string,
  ): string => {
    const label =
      failure.displayName?.trim() ||
      localName?.trim() ||
      failure.originalName.trim() ||
      'Unknown file';
    return `${label}: ${failure.error}`;
  };

  const getAcceptString = () => {
    if (accept) return accept;
    // Image-only uploads must not inherit tenant-wide allowedTypes (often includes PDFs).
    if (type === 'images') {
      return TRACKED_UPLOAD_IMAGE_ACCEPT;
    }
    if (type === 'files') {
      return allowedTypes && !allowedTypes.includes('image/*')
        ? allowedTypes
        : '.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls';
    }
    return allowedTypes || 'image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls';
  };

  const handleFileSelect = async (selectedFiles: FileList) => {
    if (disabled) return;

    let filesToUpload = Array.from(selectedFiles);

    if (type === 'images') {
      const rejected = filesToUpload.filter((f) => !isTrackedUploadAllowedImageFile(f));
      if (rejected.length > 0) {
        setError(
          rejected.length === filesToUpload.length
            ? 'Only PNG, JPEG, GIF, WebP, BMP, and TIFF images are supported.'
            : `Skipped ${rejected.length} unsupported image(s). Use PNG, JPEG, GIF, WebP, BMP, or TIFF.`,
        );
      }
      filesToUpload = filesToUpload.filter(isTrackedUploadAllowedImageFile);
      if (filesToUpload.length === 0) {
        return;
      }
    }

    // Check max files
    if (files.length + filesToUpload.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadedFiles = [] as UploadedFile[];
      
      // Upload files using tracked upload (with database tracking)
      const uploadEntityType = entityType || 'temp_fingerprint'; // Default fallback
      // Use provided fileCategory prop, or default based on type
      const uploadFileCategory = fileCategory || (type === 'images' ? 'image' : 'document');
      
      try {
        // Upload all files with database tracking
        const uploadResult = await tenantUploadService.uploadTrackedFiles(
          filesToUpload,
          uploadEntityType,
          entityId, // Use provided entityId if available
          uploadFileCategory,
          undefined, // tags
          undefined  // expiresInDays - backend handles orphaned file expiration
        );
        
        // Transform upload results to UploadedFile format
        for (const fileData of uploadResult.uploadedFiles) {
          uploadedFiles.push({
            url: '', // Will be populated with presigned URL when needed
            metadata: {
              originalName: fileData.originalName,
              mimeType: fileData.mimeType,
              size: fileData.size,
              objectKey: fileData.objectKey,
              uploadedAt: new Date(),
              ...(fileData.fileId != null ? { fileId: fileData.fileId } : {}),
            },
          });
        }

        if (uploadResult.failedUploads?.length) {
          const failureSummary = uploadResult.failedUploads
            .map((f, index) =>
              formatUploadFailureLine(f, filesToUpload[index]?.name),
            )
            .join('\n');
          setError(`Some files could not be uploaded:\n${failureSummary}`);
        }
      } catch (uploadError) {
        console.error('Failed to upload files:', uploadError);
        type PartialUploadPayload = {
          uploadedFiles?: Array<{
            originalName: string;
            mimeType: string;
            size: number;
            objectKey: string;
            fileId?: number;
          }>;
          failedUploads?: Array<{
            originalName: string;
            displayName?: string;
            error: string;
          }>;
        };
        const partial =
          uploadError instanceof Error
            ? (uploadError as Error & { uploadData?: PartialUploadPayload }).uploadData
            : undefined;
        if (partial?.uploadedFiles?.length) {
          for (const fileData of partial.uploadedFiles) {
            uploadedFiles.push({
              url: '',
              metadata: {
                originalName: fileData.originalName,
                mimeType: fileData.mimeType,
                size: fileData.size,
                objectKey: fileData.objectKey,
                uploadedAt: new Date(),
                ...(fileData.fileId != null ? { fileId: fileData.fileId } : {}),
              },
            });
          }
          const failureSummary = partial.failedUploads
            ?.map((f, index) => formatUploadFailureLine(f, filesToUpload[index]?.name))
            .join('\n');
          setError(
            failureSummary
              ? `Some files could not be uploaded:\n${failureSummary}`
              : uploadError instanceof Error
                ? uploadError.message
                : 'Some files could not be uploaded',
          );
        } else {
          setError(
            `Failed to upload files: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`,
          );
        }
      }

      if (uploadedFiles.length > 0) {
        const newFiles = [...files, ...uploadedFiles];
        onFilesChange(newFiles);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled || uploading) return;

    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleTempFilesImported = (
    items: Array<{ fileId: number; previewUrl: string; file: TempFileMetadata }>,
  ) => {
    const slotsLeft = maxFiles - files.length;
    if (slotsLeft <= 0) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const matching = items.filter(({ file }) => tempFileMatchesMediaKind(file, tempFileMediaKind));
    if (matching.length < items.length) {
      setError('Only files matching the allowed type can be imported.');
    }
    const toAdd = matching.slice(0, slotsLeft);
    if (toAdd.length < items.length) {
      setError(`Only ${toAdd.length} of ${items.length} files imported (max ${maxFiles} total).`);
    }

    const uploaded: UploadedFile[] = toAdd.map(({ fileId, previewUrl, file: tempFile }) => ({
      url: previewUrl,
      metadata: {
        originalName: tempFile.originalName,
        mimeType: tempFile.mimeType,
        size: tempFile.sizeBytes,
        objectKey: tempFile.objectKey,
        uploadedAt: new Date(tempFile.uploadedAt),
        fileId,
        fromTempFile: true,
      },
    }));

    const targetCategory = resolveTempFileTargetCategory(fileCategory, tempFileMediaKind);

    onFilesChange([...files, ...uploaded]);
    onPendingTempFileAssociationsChange?.([
      ...pendingTempFileAssociations,
      ...toAdd.map(({ fileId, previewUrl }) => ({ fileId, previewUrl, targetCategory })),
    ]);
  };

  const removeFile = async (index: number) => {
    const fileToRemove = files[index];
    const objectKey = fileToRemove.metadata.objectKey;
    const removedFileId = fileToRemove.metadata.fileId;
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);

    if (removedFileId && onPendingTempFileAssociationsChange) {
      onPendingTempFileAssociationsChange(
        pendingTempFileAssociations.filter((p) => p.fileId !== removedFileId),
      );
    }

    if (!objectKey || isStagedTempFileKey(objectKey) || fileToRemove.metadata.fromTempFile) {
      return;
    }

    try {
      const fileKind =
        fileCategory === 'document' || type === 'files' ? 'file' : 'image';

      if (entityId && (entityType === 'wafer' || entityType === 'container')) {
        await filesService.deleteEntityFile(entityType, entityId, objectKey, fileKind);
      } else {
        await tenantUploadService.deleteOrphanedFiles([objectKey]);
      }
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImage = (mimeType: string): boolean => {
    return mimeType.startsWith('image/');
  };

  const getFileIcon = (mimeType: string) => {
    return isImage(mimeType) ? <ImageIcon /> : <FileIcon />;
  };

  const theme = useTheme();

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: compact && compactHideHint ? 1 : 2 }}>
          {error}
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={getAcceptString()}
        capture={type === 'images' && captureEnvironment !== false ? 'environment' : undefined}
        multiple={multiple}
        onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
        disabled={disabled || uploading}
      />

      {compact ? (
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          {allowTempFilePick ? (
            <UploadSourceButton
              label={type === 'images' ? COMPACT_UPLOAD_LABELS.image : COMPACT_UPLOAD_LABELS.file}
              uploading={uploading}
              disabled={disabled}
              allowTempFilePick
              onSelectSource={(source) => {
                if (source === 'device') {
                  fileInputRef.current?.click();
                } else {
                  setTempFilePickerOpen(true);
                }
              }}
            />
          ) : (
            <CompactUploadButton
              label={type === 'images' ? COMPACT_UPLOAD_LABELS.image : COMPACT_UPLOAD_LABELS.file}
              uploading={uploading}
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            />
          )}
          {!compactHideHint ? (
            <Typography variant="caption" color="text.secondary">
              Max {maxFiles} · {getAcceptString()}
            </Typography>
          ) : null}
        </Box>
      ) : (
        <Paper
          variant="outlined"
          aria-busy={uploading}
          sx={{
            p: 3,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            cursor: disabled || uploading ? 'wait' : 'pointer',
            backgroundColor: disabled
              ? 'grey.100'
              : uploading
                ? alpha(theme.palette.primary.main, 0.08)
                : 'transparent',
            borderWidth: uploading ? 2 : 1,
            borderStyle: uploading ? 'solid' : 'dashed',
            borderColor: uploading ? 'primary.main' : 'divider',
            animation: uploading ? 'fileUploadZonePulse 1.4s ease-in-out infinite' : 'none',
            '@keyframes fileUploadZonePulse': {
              '0%, 100%': {
                boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.35)}`,
              },
              '50%': {
                boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0)}`,
              },
            },
            '&:hover': {
              backgroundColor:
                disabled || uploading
                  ? uploading
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'grey.100'
                  : 'action.hover',
              borderColor: disabled || uploading ? undefined : 'primary.light',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <Box>
              <CircularProgress size={44} thickness={4} color="primary" />
              <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: 'primary.main' }}>
                Uploading files…
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Please wait
              </Typography>
              <LinearProgress color="primary" sx={{ mt: 2, borderRadius: 1, maxWidth: 280, mx: 'auto' }} />
            </Box>
          ) : (
            <Box>
              <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {type === 'images' ? 'Upload Images' : type === 'files' ? 'Upload Files' : 'Upload Files & Images'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag and drop files here, or click to select
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Maximum {maxFiles} files • {getAcceptString()}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* File list */}
      {files.length > 0 && showPreview && shouldShowFileList && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Uploaded Files ({files.length})
          </Typography>
          <Stack spacing={1}>
            {files.map((file, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                    {getFileIcon(file.metadata.mimeType)}
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" noWrap title={file.metadata.originalName}>
                        {file.metadata.originalName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.metadata.size)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {isImage(file.metadata.mimeType) && (
                      <Chip size="small" label="Image" color="primary" variant="outlined" />
                    )}
                    <AppIconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      disabled={disabled}
                    >
                      <Delete fontSize="small" />
                    </AppIconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {allowTempFilePick ? (
        <TempFilePickerDialog
          open={tempFilePickerOpen}
          onClose={() => setTempFilePickerOpen(false)}
          mediaKind={tempFileMediaKind}
          title={tempFilePickerTitle}
          maxSelection={Math.max(0, maxFiles - files.length)}
          onImport={handleTempFilesImported}
        />
      ) : null}
    </Box>
  );
};

export default FileUpload;